export const COMPLETION_RESOLUTION_OPTIONS = [
  { value: 'resolved', label: 'Resolved' },
  { value: 'partial', label: 'Partial' },
  { value: 'rescheduled', label: 'Rescheduled' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export type CompletionResolution = typeof COMPLETION_RESOLUTION_OPTIONS[number]['value'];

type ConfirmationVariant = 'success' | 'info' | 'warning';

export function normalizeCompletionResolution(value?: string | null): CompletionResolution {
  if (typeof value !== 'string') {
    return 'resolved';
  }

  return (COMPLETION_RESOLUTION_OPTIONS as readonly { value: string }[]).some((option) => option.value === value)
    ? value as CompletionResolution
    : 'resolved';
}

export function requiresPublicCommentForCompletionResolution(resolution: CompletionResolution): boolean {
  return resolution === 'resolved' || resolution === 'partial';
}

/** When comments are required by appointment/job rules, cancelled & rescheduled need an internal note instead of closing details. */
export function requiresInternalCommentForCompletionResolution(resolution: CompletionResolution): boolean {
  return resolution === 'cancelled' || resolution === 'rescheduled';
}

/** Customer signature is only required when the visit is closed out as resolved or partial — not for cancelled / rescheduled. */
export function requiresSignatureForCompletionResolution(resolution: CompletionResolution): boolean {
  return resolution === 'resolved' || resolution === 'partial';
}

/** Completion photos are only required when the visit is closed out as resolved or partial — not for cancelled / rescheduled. */
export function requiresPhotosForCompletionResolution(resolution: CompletionResolution): boolean {
  return resolution === 'resolved' || resolution === 'partial';
}

export function canResolveParentJobAfterAppointment(resolution: CompletionResolution): boolean {
  return resolution === 'resolved';
}

export function getCompletionPersistedStatus(itemType: string | null | undefined, resolution: CompletionResolution): 'resolved' | 'unresolved' | 'cancelled' | 'rescheduled' | 'scheduled' {
  if (itemType === 'appointment') {
    return resolution === 'resolved' ? 'resolved' : 'unresolved';
  }

  if (resolution === 'cancelled') {
    return 'cancelled';
  }

  if (resolution === 'rescheduled') {
    return itemType === 'appointment' ? 'rescheduled' : 'scheduled';
  }

  return 'resolved';
}

export function getCompletionToastActionLabel(
  resolution: CompletionResolution,
  itemType?: string | null,
): string {
  if (itemType === 'appointment' && resolution !== 'resolved') {
    switch (resolution) {
      case 'partial':
        return 'marked unresolved (partial)';
      case 'rescheduled':
        return 'marked unresolved (rescheduled)';
      case 'cancelled':
        return 'marked unresolved (cancelled)';
      default:
        break;
    }
  }

  switch (resolution) {
    case 'partial':
      return 'partially resolved';
    case 'rescheduled':
      return 'rescheduled';
    case 'cancelled':
      return 'cancelled';
    case 'resolved':
    default:
      return 'resolved';
  }
}

export function getCompletionResolutionSummary(resolution: CompletionResolution, isAppointment: boolean): string {
  switch (resolution) {
    case 'partial':
      return isAppointment
        ? 'Appointment will be marked as unresolved with a partial resolution and the parent job will stay open'
        : 'Job will be saved as partially resolved — admin review is still required to close';
    case 'rescheduled':
      return isAppointment
        ? 'Appointment will be marked as unresolved and flagged for rescheduling'
        : 'Job will be returned to scheduled so a new appointment can be arranged';
    case 'cancelled':
      return isAppointment
        ? 'Appointment will be marked as unresolved and flagged as cancelled'
        : 'Job will be marked as cancelled';
    case 'resolved':
    default:
      return isAppointment
        ? 'Appointment will be marked as resolved'
        : 'Job will be set to Resolved — requires admin approval to close';
  }
}

export function getCompletionConfirmConfig({
  resolution,
  itemLabel,
  itemTitle,
  parentResolveNote = '',
  isAppointment = false,
}: {
  resolution: CompletionResolution;
  itemLabel: string;
  itemTitle: string;
  parentResolveNote?: string;
  isAppointment?: boolean;
}): {
  title: string;
  message: string;
  confirmText: string;
  variant: ConfirmationVariant;
} {
  switch (resolution) {
    case 'partial':
      return {
        title: `Save Partial ${itemLabel}`,
        message: isAppointment
          ? `Mark "${itemTitle}" as unresolved with a partial resolution?${parentResolveNote}`
          : `Mark "${itemTitle}" as partially resolved?${parentResolveNote}`,
        confirmText: 'Save Partial',
        variant: 'info',
      };
    case 'rescheduled':
      return {
        title: `Reschedule ${itemLabel}`,
        message: isAppointment
          ? `Mark "${itemTitle}" as unresolved and flag it as rescheduled?`
          : `Mark "${itemTitle}" as rescheduled?`,
        confirmText: 'Reschedule',
        variant: 'info',
      };
    case 'cancelled':
      return {
        title: `Cancel ${itemLabel}`,
        message: isAppointment
          ? `Mark "${itemTitle}" as unresolved and flag it as cancelled?`
          : `Mark "${itemTitle}" as cancelled?`,
        confirmText: `Cancel ${itemLabel}`,
        variant: 'warning',
      };
    case 'resolved':
    default:
      return {
        title: `Resolve ${itemLabel}`,
        message: `Mark "${itemTitle}" as resolved?${parentResolveNote}`,
        confirmText: 'Resolve',
        variant: 'success',
      };
  }
}
